import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@context/AuthContext";
import { useRole } from "@hooks/useRole";
import { Modal, ModalFooter } from "@common/Modal";
import logoIcon from "@assets/icons/logo_icon.png";

export default function Sidebar({ collapsed, mobileOpen, onClose }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const { user, userSlug, logout } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const { role, isFirmOwner, isRM, isResource, hasAnyRole, ROLES, firmType, isStaffingFirm } = useRole();

  const initials = user?.first_name
    ? `${user.first_name[0]}${user.last_name?.[0] || ""}`.toUpperCase()
    : "U";

  // ── Exact nav logic from old LeftMenu.js ───────────────────────────────────
  const NAV = [
    // Dashboard — always shown; role 3 gets "My Profile" pointing at their
    // own profile view (not the generic recruiter dashboard, which showed
    // irrelevant widgets like Total Resources / My Network for a candidate)
    {
      label: role === ROLES.RESOURCE ? "My Profile" : "Dashboard",
      icon:  role === ROLES.RESOURCE ? "fa-gear" : "fa-table-cells-large",
      path:  role === ROLES.RESOURCE ? `/profile/${userSlug}` : "/dashboard",
      colorClass: "blue",
      show: true,
    },
    // Search — roles 1,2,4 always; role 3 only if firmType=1
    {
      label: "Search",
      icon:  "fa-magnifying-glass",
      path:  "/search",
      colorClass: "green",
      show: hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT]) || (isResource && isStaffingFirm),
    },
    // My Team / My Candidates — roles 1 and 2 only; label/path depends on firmType
    {
      label: isStaffingFirm ? "My Team" : "My Candidates",
      icon:  "fa-users",
      path:  isStaffingFirm ? "/my-team" : "/resources",
      colorClass: "amber",
      show: hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM]),
    },
    // Resource Managers — role 1 only
    {
      label: "Resource Managers",
      icon:  "fa-shield-halved",
      path:  "/resource-managers",
      colorClass: "indigo",
      show: isFirmOwner,
    },
    // My Profile — role 2 only + firmType 1
    {
      label: "My Profile",
      icon:  "fa-circle-user",
      path:  `/my-resource-profile/details/${userSlug}`,
      colorClass: "teal",
      show: isRM && isStaffingFirm,
    },
    // My Clients — role 2 only
    {
      label: "My Clients",
      icon:  "fa-link",
      path:  "/access-control",
      colorClass: "pink",
      show: isRM,
    },
    // Job Posting — roles 1, 2, 4 only (NO role 3, NO "My Jobs" item)
    {
      label: "Job Posting",
      icon:  "fa-briefcase",
      path:  "/job/latest-jobs",
      colorClass: "orange",
      show: hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT]),
    },
    // ConnectIQ AI — role 2 only (new feature)
    {
      label: "Connect IQ AI",
      icon:  "fa-robot",
      path:  "/connectiq",
      colorClass: "purple",
      show: isRM,
    },
    // Edit Firm — role 1 only
    {
      label: "Edit Firm",
      icon:  "fa-building-user",
      path:  "/edit-firm",
      colorClass: "slate",
      show: isFirmOwner,
    },
    // Settings — roles 1, 2, 4 (matches old app's separate Settings link;
    // role 3 has their own profile page instead, reached via "My Profile" above)
    {
      label: "Settings",
      icon:  "fa-gear",
      path:  "/account-settings",
      colorClass: "slate",
      show: hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT]),
    },
  ].filter(n => n.show);

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if (path === "/dashboard") return false;
    // Match sub-paths
    if (path === "/resources" || path === "/my-team")
      return location.pathname.startsWith("/resources") || location.pathname.startsWith("/my-team");
    if (path === "/job/latest-jobs")
      return location.pathname.includes("/job") || location.pathname.includes("/create-job");
    if (path === "/resource-managers")
      return location.pathname.includes("/resource-manager");
    if (path.includes("/my-resource-profile"))
      return location.pathname.includes("/my-resource-profile");
    return location.pathname.startsWith(path) && path.length > 1;
  };

  const NavItem = ({ item }) => {
    const active = isActive(item.path);
    return (
      <Link to={item.path} onClick={onClose}
        title={collapsed ? item.label : undefined}
        className="fc-nav-item"
        style={{
          justifyContent: collapsed ? "center" : "flex-start",
          background:  active ? "rgba(79,70,229,0.18)" : "transparent",
          borderLeft:  active ? "3px solid #60A5FA" : "3px solid transparent",
          paddingLeft: collapsed ? 0 : 12,
          paddingRight: collapsed ? 0 : 10,
          color:      active ? "var(--fc-sidebar-text-strong)" : "var(--fc-sidebar-text)",
          fontWeight: active ? 600 : 400,
          borderRadius: "0 10px 10px 0",
          marginRight: 8,
        }}>
        <div className={`fc-nav-icon fc-nav-bg-${item.colorClass}`} style={{ flexShrink: 0 }}>
          <i className={`fa-solid ${item.icon} fc-nav-color-${item.colorClass}`} />
        </div>
        {!collapsed && <span className="flex-grow-1" style={{ fontSize: 13.5 }}>{item.label}</span>}
        {active && !collapsed && (
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#60A5FA", flexShrink: 0 }} />
        )}
      </Link>
    );
  };

  return (
    <aside className={`fc-sidebar${collapsed ? " collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}>

      {/* Header — logo */}
      <div className="fc-sidebar-header">
        <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none overflow-hidden flex-grow-1">
          <img src={logoIcon} alt="FC" width={28} height={28} style={{ objectFit: "contain", flexShrink: 0, borderRadius: 6 }} />
          {!collapsed && (
            <span style={{ fontSize: 15, fontWeight: 800, color: "var(--fc-sidebar-text-strong)", whiteSpace: "nowrap", letterSpacing: "-0.02em" }}>
              Firm<span style={{ color: "#60A5FA" }}>Connector</span>
            </span>
          )}
        </Link>
        <button className="d-lg-none" onClick={onClose} aria-label="Close menu"
          style={{ background: "none", border: "none", color: "var(--fc-sidebar-text-muted)", cursor: "pointer", fontSize: 18, padding: "4px 6px" }}>
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: collapsed ? "12px 0 12px 4px" : "12px 0", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none" }}>
        <style>{`.fc-sidebar nav::-webkit-scrollbar{display:none}`}</style>
        {!collapsed && (
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--fc-sidebar-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", padding: "0 16px", marginBottom: 8 }}>
            Menu
          </div>
        )}
        {NAV.map(item => <NavItem key={item.path} item={item} />)}
        <div style={{ height: 1, background: "var(--fc-sidebar-divider)", margin: collapsed ? "10px 8px" : "10px 16px 10px 4px" }} />
      </nav>

      {/* Footer */}
      <div style={{ padding: collapsed ? "10px 8px" : "10px 12px", borderTop: "1px solid var(--fc-sidebar-divider)", flexShrink: 0 }}>
        {collapsed ? (
          <div className="d-flex flex-column align-items-center gap-2">
            <div className="fc-user-avatar">{initials}</div>
            <button onClick={() => setConfirmLogout(true)} aria-label="Sign out"
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--fc-sidebar-text-muted)", padding: "3px", transition: "color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.color = "#DC2626"}
              onMouseLeave={e => e.currentTarget.style.color = "var(--fc-sidebar-text-muted)"}>
              <i className="fa-solid fa-right-from-bracket" style={{ fontSize: 14 }} />
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "var(--fc-sidebar-card-bg)", border: "1px solid var(--fc-sidebar-card-border)" }}>
            <div className="fc-user-avatar">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--fc-sidebar-text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user?.first_name} {user?.last_name}
              </div>
              <div style={{ fontSize: 11, color: "var(--fc-sidebar-text-muted)" }}>
                {role === ROLES.FIRM_OWNER ? "Firm Owner" : role === ROLES.RM ? "Resource Manager" : role === ROLES.RESOURCE ? "Resource" : role === ROLES.CLIENT ? "Client" : "Member"}
              </div>
            </div>
            <button onClick={() => setConfirmLogout(true)} aria-label="Sign out"
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--fc-sidebar-text-muted)", padding: "3px", flexShrink: 0, transition: "color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.color = "#DC2626"}
              onMouseLeave={e => e.currentTarget.style.color = "var(--fc-sidebar-text-muted)"}>
              <i className="fa-solid fa-right-from-bracket" style={{ fontSize: 14 }} />
            </button>
          </div>
        )}
      </div>

      {confirmLogout && (
        <Modal title="Sign out?" onClose={() => setConfirmLogout(false)} maxWidth={380}
          footer={<ModalFooter onCancel={() => setConfirmLogout(false)}
            onConfirm={() => { logout(); navigate("/"); }}
            confirmLabel="Sign out" confirmTone="danger" />}>
          <p style={{ fontSize: 13, color: "var(--fc-text-secondary)" }}>You'll need to log back in to access your account.</p>
        </Modal>
      )}
    </aside>
  );
}